import React, { useMemo } from 'react'
import { usePaletteTheme } from '../../context/paletteTheme'
import { Group } from '@visx/group'
import { AreaClosed, LinePath } from '@visx/shape'
import { AxisLeft, AxisBottom } from '@visx/axis'
import { LinearGradient } from '@visx/gradient'
import { curveLinear } from '@visx/curve'

import { getDate, getValue } from './graphUtils'

const xTickFormat = (d) => {
  return d.getDate() > 1
    ? new Date(d).toLocaleDateString()
    : new Date(d).toLocaleDateString(undefined,{ year: 'numeric', month: 'long'})
}
const visiblePoints = (data, domain) =>  data
  .filter(d => domain[0] <= getDate(d) && domain[1] >= getDate(d))
  .length

export default function AreaChart({
  data,
  color,
  width,
  yMax,
  margin = { top: 0, left: 0, bottom: 0, right: 0},
  xScale,
  yScale,
  hideBottomAxis = false,
  hideLeftAxis = false,
  top,
  left,
  children,
  area = true,
  line = true,
  strokeDasharray=null
}) {
  const { colors } = usePaletteTheme()
  const uuid = useMemo(() => Math.random(), [])
  const axisColor = colors.textPrimary
  
  if (width < 10) return null
  const numXTicks = Math.floor(width / 180)
  const isDotted = width / visiblePoints(data, xScale.domain()) > 60

  // Initialize some variables
  const axisBottomTickLabelProps = () => ({
    textAnchor: 'middle',
    fontFamily: 'Arial',
    fontSize: 10,
    fill: axisColor,
  })

  const axisLeftTickLabelProps = () => ({
    dx: '-0.25em',
    dy: '0.25em',
    fontFamily: 'Arial',
    fontSize: 10,
    textAnchor: 'end',
    fill: axisColor,
  })
  
  return (
    <Group left={left || margin.left} top={top || margin.top}>
      { area && (<>
        <LinearGradient
          id={`gradient-${uuid}`}
          from={color}
          fromOpacity={0.4}
          to={color}
          toOpacity={0}
        />
        <AreaClosed
          data={data}
          x={d => xScale(getDate(d)) }
          y={d => yScale(getValue(d)) }
          yScale={yScale}
          fill={`url(#gradient-${uuid})`}
          curve={curveLinear}
        />
      </>)}
      { line && (
          <LinePath
            data={data}
            x={d => xScale(getDate(d)) }
            y={d => yScale(getValue(d)) }
            strokeWidth={1}
            stroke={color}
            strokeDasharray={strokeDasharray} 
            curve={curveLinear}
          />
        )}
      { isDotted &&
          data.map((d, i) => (
            <circle
              key={i}
              r={2}
              cx={xScale(getDate(d))}
              cy={yScale(getValue(d))}
              fill={color}
            />
        ))}
      { !hideBottomAxis && (
          <AxisBottom
            top={yMax}
            scale={xScale}
            numTicks={numXTicks}
            stroke={axisColor}
            tickStroke={axisColor}
            tickLabelProps={axisBottomTickLabelProps}
            tickFormat={xTickFormat}
          />
      )}
      { !hideLeftAxis && (
          <AxisLeft
            scale={yScale}
            numTicks={5}
            stroke={axisColor}
            tickStroke={axisColor}
            tickLabelProps={axisLeftTickLabelProps}
          />
      )}
      {children}
    </Group>
  )
}
